import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';

import type { ContentAsset } from '@mds/shared/types/content';

import * as files from '@/files/files.api';

/**
 * Picture URLs for a set of stored-file ids.
 *
 * The page read resolves every file the saved draft references, but a picture
 * picked in this session is not in that list until it is saved. Those few are
 * looked up one by one, so the form and the preview can draw them straight away.
 */
export function useAssetUrls(ids: readonly string[], known: readonly ContentAsset[]) {
  const knownMap = useMemo(() => new Map(known.map((a) => [a.fileId, a.contentUrl])), [known]);
  const missing = useMemo(
    () => [...new Set(ids)].filter((id) => !knownMap.has(id)),
    [ids, knownMap],
  );

  const lookups = useQueries({
    queries: missing.map((id) => ({
      queryKey: ['files', 'one', id],
      queryFn: () => files.getFile(id),
      staleTime: 5 * 60_000,
    })),
  });

  return useMemo(() => {
    const urls = new Map(knownMap);
    lookups.forEach((q, i) => {
      const url = q.data?.contentUrl;
      if (url) urls.set(missing[i] as string, url);
    });
    return urls;
  }, [knownMap, lookups, missing]);
}
